import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import AchievementsPage from './pages/AchievementsPage';
import PositionsPage from './pages/PositionsPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import AuthPage from './pages/AuthPage';
import EndorsePage from './pages/EndorsePage';
import ProtectedRoute from './components/ProtectedRoute';
import { UserProvider } from './contexts/UserContext';

function App() {
  return (
    <UserProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/endorse/:token" element={<EndorsePage />} />
          <Route path="/u/:slug" element={<PublicProfilePage />} />

          {/* Protected app routes — all wrapped in Layout */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <AppRoutes />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </UserProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/achievements" replace />} />
      <Route path="/achievements" element={<AchievementsPageWrapper />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/dashboard" element={<ComingSoon label="Dashboard" />} />
      <Route path="/positions" element={<PositionsPage />} />
      <Route path="/skills" element={<ComingSoon label="Skills" />} />
      <Route path="/settings" element={<ComingSoon label="Settings" />} />
      <Route path="*" element={<Navigate to="/achievements" replace />} />
    </Routes>
  );
}

function ComingSoon({ label }) {
  return (
    <div className="p-8 text-center text-gray-500">{label} — Coming Soon</div>
  );
}

// AchievementsPage manages its own data so we keep it self-contained
function AchievementsPageWrapper() {
  const [achievements, setAchievements] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [reviewDate, setReviewDate] = React.useState(null);

  const loadAchievements = React.useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const { default: api } = await import('./services/apiService');
      const data = await api.getAchievements();
      setAchievements(data);
    } catch (err) {
      if (!silent) setError('Failed to load achievements. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch profile once to get review date — runs alongside achievement load
  React.useEffect(() => {
    import('./services/apiService').then(({ default: api }) =>
      api.getProfile().then(res => {
        const p = res?.data || res;
        if (p?.next_review_date) setReviewDate(p.next_review_date.split('T')[0]);
      }).catch(() => {})
    );
  }, []);

  React.useEffect(() => { loadAchievements(); }, [loadAchievements]);

  const handleAddAchievement = async (achievementData) => {
    const { default: api } = await import('./services/apiService');
    const newAchievement = await api.createAchievement(achievementData);
    setAchievements(prev => [newAchievement, ...prev]);
    return newAchievement;
  };

  const handleEditAchievement = async (achievementId, achievementData) => {
    const { default: api } = await import('./services/apiService');
    const result = await api.updateAchievement(achievementId, achievementData);
    const { _meta, ...updatedAchievement } = result;
    setAchievements(prev =>
      prev.map(a => a.id === achievementId ? updatedAchievement : a)
    );
    if (_meta?.endorsementWasReset) {
      alert('Achievement updated. It will need to be re-endorsed by your manager.');
    }
    return updatedAchievement;
  };

  const handleDeleteAchievement = async (achievementId) => {
    setAchievements(prev => prev.filter(a => a.id !== achievementId));
    try {
      const { default: api } = await import('./services/apiService');
      await api.deleteAchievement(achievementId);
    } catch {
      await loadAchievements();
      alert('Failed to delete achievement. Please try again.');
    }
  };

  const handleRequestEndorsement = async (achievementId) => {
    const { default: api } = await import('./services/apiService');
    await api.addEndorsement(achievementId);
    await loadAchievements();
    alert('Endorsement request sent!');
  };

  const handleAddWitnesses = async (achievementId, witnessData) => {
    const { default: api } = await import('./services/apiService');
    await api.addWitness(achievementId, witnessData);
    await loadAchievements();
    alert('Witness added successfully!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-500">Loading achievements...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-sm">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadAchievements}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <AchievementsPage
      achievements={achievements}
      onAddAchievement={handleAddAchievement}
      onEditAchievement={handleEditAchievement}
      onDeleteAchievement={handleDeleteAchievement}
      onRequestEndorsement={handleRequestEndorsement}
      onAddWitnesses={handleAddWitnesses}
      onRefreshAchievements={() => loadAchievements(true)}
      reviewDate={reviewDate}
    />
  );
}

export default App;
